<script>
	import { nanoid } from "nanoid";
	import { onMount, createEventDispatcher } from "svelte";

	import DatePickerNavBar from "./DatePickerNavBar.svelte";
	import Popover from "./Popover.svelte";
	import { getMonths } from "./lib/helpers";
	import { keyCodes, keyCodesArray } from "./lib/keyCodes";
	import DatePickerNavBarMonth from "./DatePickerNavBarMonth.svelte";

	const dispatch = createEventDispatcher();
	const today = new Date();
	let popover;

	export let format = "en-GB";
	export let label = null;
	export let sublabel = null;
	export let selected = null;
	export let formattedSelected;
	if (formattedSelected && !isFormattedMonthDate(formattedSelected)) {
		formattedSelected = null;
	}
	if (formattedSelected && selected === null) {
		selected = unformatMonthDate(formattedSelected, format);
	}

	export let openOnNodeType;
	export let openOnClass;
	export let start = new Date(1920, 1, 1);
	export let end = new Date(2100, 1, 1);
	export let placeholder = "e.g. 01/2024";
	export let monthChosen = false;
	export let trigger = null;
	export let selectableCallback = null;
	export let style = "";
	export let monthsOfYear = [
		["January", "Jan"],
		["February", "Feb"],
		["March", "Mar"],
		["April", "Apr"],
		["May", "May"],
		["June", "Jun"],
		["July", "Jul"],
		["August", "Aug"],
		["September", "Sep"],
		["October", "Oct"],
		["November", "Nov"],
		["December", "Dec"]
	];

	// theming variables:
	export let buttonBackgroundColor = "#fff";
	export let buttonBorderColor = "#eee";
	export let buttonTextColor = "#333";
	export let highlightColor = "#f7901e";
	export let monthBackgroundColor = "none";
	export let monthTextColor = "#4a4a4a";
	export let monthHighlightedBackgroundColor = "#efefef";
	export let monthHighlightedTextColor = "#4a4a4a";
	export let defaultToday = false;
	export let defaultHighlighted = null;
	export let validator;
	export let target = ".date-picker-error";
	export let required;
	export let disabled;
	export let id = nanoid(6);

	let highlighted = today;
	let shouldShakeMonth = false;
	let shakeHighlightTimeout;
	let month = today.getMonth();
	let year = today.getFullYear();

	let isOpen = false;
	let isClosing = false;
	let monthInput = false;

	today.setHours(0, 0, 0, 0);
	selected = new Date(selected) == "Invalid Date" ? (defaultToday ? today : null) : selected;

	function assignmentHandler(formatted) {
		if (!trigger) return;
		trigger.innerHTML = formatted;
	}

	$: months = getMonths(start, end, selectableCallback);

	let monthIndex = 0;
	$: {
		monthIndex = 0;
		for (let i = 0; i < months.length; i += 1) {
			if (months[i].month === month && months[i].year === year) {
				monthIndex = i;
			}
		}
	}
	$: visibleMonth = months[monthIndex];

	$: visibleMonthId = year + month / 100;
	$: canIncrementMonth = monthIndex < months.length - 1;
	$: canDecrementMonth = monthIndex > 0;
	$: wrapperStyle = `
    --button-background-color: ${buttonBackgroundColor};
    --button-border-color: ${buttonBorderColor};
    --button-text-color: ${buttonTextColor};
    --highlight-color: ${highlightColor};
    --month-background-color: ${monthBackgroundColor};
    --month-text-color: ${monthTextColor};
    --month-highlighted-background-color: ${monthHighlightedBackgroundColor};
    --month-highlighted-text-color: ${monthHighlightedTextColor};
    ${style}
  `;

	function formatMonthDate(date, format) {
		if (!date || typeof date !== "object" || !date.getFullYear || isNaN(date.getFullYear()) || !format) {
			return null;
		}

		let month = date.getMonth() + 1;
		let year = date.getFullYear();

		if ((format || "en-GB") === "en-US") {
			return `${month < 10 ? "0" + month : "" + month}/${year}`;
		} else {
			return `${month < 10 ? "0" + month : "" + month}/${year}`;
		}
	}

	function isFormattedMonthDate(formattedDate, format) {
		if (!formattedDate || typeof formattedDate === "object") {
			return false;
		}

		const components = formattedDate.split("/");

		if (components.length !== 2) {
			return false;
		}

		let date;
		let month;
		if ((format || "en-GB") === "en-US") {
			date = new Date(components[1], components[0] - 1, 1);
			month = components[0] - 1;
		} else {
			date = new Date(components[1], components[0] - 1, 1);
			month = components[0] - 1;
		}

		return date.getMonth() == month;
	}

	function unformatMonthDate(formattedDate, format) {
		if (!formattedDate || typeof formattedDate === "object") return formattedDate;

		const components = formattedDate.split("/");

		if ((format || "en-GB") === "en-US") {
			return new Date(components[1], components[0] - 1, 1);
		} else {
			return new Date(components[1], components[0] - 1, 1);
		}
	}

	$: {
		if (selected) {
			formattedSelected = formatMonthDate(selected, format);
		} else {
			formattedSelected = null;
		}
	}

	onMount(() => {
		let defaultDate = defaultHighlighted ? new Date(defaultHighlighted) : new Date();
		if (defaultDate > end) defaultDate = end;
		if (defaultDate < start) defaultDate = start;
		month = selected == null ? defaultDate.getMonth() : selected.getMonth();
		year = selected == null ? defaultDate.getFullYear() : selected.getFullYear();
	});

	function handleFocus() {
		if (isOpen) {
			popover.close();
		}
	}

	function changeMonth(selectedMonth) {
		month = selectedMonth;
		registerSelection();
	}

	function changeYear(selectedYear) {
		year = selectedYear;
	}

	function incrementMonth(direction) {
		if (direction === 1 && !canIncrementMonth) return;
		if (direction === -1 && !canDecrementMonth) return;

		let current = new Date(year, month, 1);
		current.setMonth(current.getMonth() + direction);
		month = current.getMonth();
		year = current.getFullYear();
		highlighted = new Date(year, month, 1);
	}

	function incrementYear({ direction, amount }) {
		if (direction === 1 && year == end.getFullYear()) return;
		if (direction === -1 && year == start.getFullYear()) return;

		let current = new Date(year, month, 1);
		current.setYear(current.getFullYear() + amount * direction);
		month = current.getMonth();
		year = current.getFullYear();
		highlighted = new Date(year, month, 1);
	}

	function getDefaultHighlighted() {
		if (selected) return new Date(selected);
		if (defaultHighlighted) return new Date(defaultHighlighted);

		let defaultDate = new Date();
		if (defaultDate > end) defaultDate = end;
		if (defaultDate < start) defaultDate = start;

		return defaultDate;
	}

	function shakeMonth() {
		clearTimeout(shakeHighlightTimeout);
		shouldShakeMonth = true;
		shakeHighlightTimeout = setTimeout(() => {
			shouldShakeMonth = false;
		}, 700);
	}

	function assignValueToTrigger(formatted) {
		assignmentHandler(formatted);
	}

	function registerSelection() {
		// Generate date from current month and year
		let chosen = new Date(year, month, 1);

		if (chosen < start || chosen > end) return shakeMonth();

		close();
		selected = chosen;
		monthChosen = true;
		assignValueToTrigger(formattedSelected);
		let formattedDate = formatMonthDate(selected, format);

		if (validator) {
			if (selected) {
				monthInput.value = formattedDate;
			}

			var evt = new Event("change");
			monthInput.dispatchEvent(evt);
		}

		return dispatch("monthSelected", { date: chosen, formattedDate: formattedDate });
	}

	function handleKeyPress(evt) {
		if (keyCodesArray.indexOf(evt.keyCode) === -1) return;
		evt.preventDefault();
		switch (evt.keyCode) {
			case keyCodes.left:
				incrementMonth(-1);
				break;
			case keyCodes.right:
				incrementMonth(1);
				break;
			case keyCodes.pgup:
				incrementYear({ direction: -1, amount: 1 });
				break;
			case keyCodes.pgdown:
				incrementYear({ direction: 1, amount: 1 });
				break;
			case keyCodes.escape:
				close();
				break;
			case keyCodes.enter:
			case keyCodes.tab:
				registerSelection();
				break;
			default:
				break;
		}
	}

	function registerClose() {
		document.removeEventListener("keydown", handleKeyPress);
		dispatch("close");
	}

	function close() {
		popover.close();
		registerClose();
	}

	function registerOpen() {
		highlighted = getDefaultHighlighted();
		month = selected == null ? highlighted.getMonth() : selected.getMonth();
		year = selected == null ? highlighted.getFullYear() : selected.getFullYear();
		document.addEventListener("keydown", handleKeyPress);

		if (document.activeElement) document.activeElement.blur();

		dispatch("open");
	}

	function valueChanged(e) {
		if (e.target.value === "") {
			selected = null;
			return;
		}

		if (!isFormattedMonthDate(e.target.value)) {
			e.target.value = formattedSelected;
			return;
		}

		let chosen = unformatMonthDate(e.target.value);
		if (chosen < start || chosen > end) {
			e.target.value = formattedSelected;
		} else {
			selected = chosen;
			dispatch("dateChange", { target: e.target });

			let formattedDate = formatMonthDate(selected, format);
			dispatch("monthSelected", { date: selected, formattedDate: formattedDate });
		}
	}
</script>

<div class="datepicker" class:open={isOpen} class:closing={isClosing} style={wrapperStyle}>
	<Popover
		bind:this={popover}
		bind:open={isOpen}
		bind:shrink={isClosing}
		{trigger}
		{openOnNodeType}
		{openOnClass}
		on:opened={registerOpen}
		on:closed={registerClose}
		{disabled}>
		<div slot="trigger">
			<slot>
				{#if !trigger}
					{#if label}
						<label for={id}>
							{label}

							{#if required}
								<span class="required">*</span>
							{/if}

							{#if sublabel}
								<div class="sublabel">{sublabel}</div>
							{/if}
						</label>
					{/if}
					<div class="date-group {id}">
						<span class="calendar-pop" class:calendarpop={!disabled} id="basic-addon1">
							<i class="calendar-icon" class:calendarpop={!disabled}></i>
						</span>
						<input
							{disabled}
							{id}
							bind:this={monthInput}
							data-validator={validator}
							{required}
							type="text"
							class="form-control calendar-input"
							{placeholder}
							data-bouncer-target={target}
							aria-label="Month"
							on:change={(e) => valueChanged(e)}
							on:focus={(e) => handleFocus(e)}
							value={formattedSelected} />
						<div class="date-picker-error"></div>
					</div>
				{/if}
			</slot>
		</div>
		<div slot="contents">
			<div class="calendar">
				<DatePickerNavBarMonth
					{month}
					{year}
					{start}
					{end}
					{canIncrementMonth}
					{canDecrementMonth}
					{monthsOfYear}
					on:monthSelected={(e) => changeMonth(e.detail)}
					on:yearSelected={(e) => changeYear(e.detail)}
					on:incrementMonth={(e) => incrementMonth(e.detail)}
					on:incrementYear={(e) => incrementYear(e.detail)} />

				<div class="month-grid">
					{#each monthsOfYear as monthData, index}
						<div
							class="month-item"
							class:selected={month === index}
							on:click={() => {
								month = index;
								registerSelection();
							}}>
							{monthData[0]}
						</div>
					{/each}
				</div>
			</div>
		</div>
	</Popover>
</div>

<style>
	.date-group {
		position: relative;
	}

	.date-group > .form-control:focus {
		z-index: auto;
	}

	*,
	*:before,
	*:after {
		box-sizing: inherit;
	}

	.calendar {
		box-sizing: border-box;
		position: relative;
		overflow: hidden;
		user-select: none;
		width: 100vw;
		padding: 10px;
		padding-top: 0;
	}

	@media (min-width: 480px) {
		.calendar {
			height: auto;
			width: 340px;
			max-width: 100%;
		}
	}

	.month-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 10px;
		margin-top: 15px;
		padding: 5px;
	}

	.month-item {
		padding: 12px 0;
		text-align: center;
		cursor: pointer;
		border-radius: 4px;
		background-color: var(--month-background-color);
		color: var(--month-text-color);
	}

	.month-item:hover {
		background-color: var(--month-highlighted-background-color);
		color: var(--month-highlighted-text-color);
	}

	.month-item.selected {
		background-color: var(--highlight-color);
		color: white;
	}

	.calendar-pop {
		cursor: default;
		position: absolute;
		right: 10px;
		top: 2px;
		line-height: 50px;
	}

	.calendar-pop.calendarpop {
		cursor: pointer;
	}

	.calendar-icon {
		display: inline-block;
		font: normal normal normal 14px/1 FontAwesome;
		font-size: inherit;
		text-rendering: auto;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	.calendar-icon:before {
		content: "\f073";
	}

	.date-picker-error {
		width: 100%;
	}
</style>
